/**
 * Árvore de comportamento mínima (item 5.1). Cada nó olha o contexto e diz se deu certo. O seletor tenta os
 * filhos em ordem até um dar certo; a sequência precisa de todos. A árvore roda inteira a cada decisão do bot,
 * então não há estado "rodando": a memória do bot guarda o que precisa durar mais de uma decisão.
 */
export type Status = "sucesso" | "falha";

export interface BtNode<C> {
  name: string;
  tick(c: C): Status;
}

/** Contexto mínimo: a árvore anota o nome da ação escolhida, para depuração e testes. */
export interface Traced {
  trace: string[];
}

export const selector = <C>(name: string, ...children: BtNode<C>[]): BtNode<C> => ({
  name,
  tick: (c) => (children.some((n) => n.tick(c) === "sucesso") ? "sucesso" : "falha"),
});

export const sequence = <C>(name: string, ...children: BtNode<C>[]): BtNode<C> => ({
  name,
  tick: (c) => (children.every((n) => n.tick(c) === "sucesso") ? "sucesso" : "falha"),
});

export const condition = <C>(name: string, test: (c: C) => boolean): BtNode<C> => ({
  name,
  tick: (c) => (test(c) ? "sucesso" : "falha"),
});

/** Ação: faz algo e sempre dá certo. */
export const action = <C extends Traced>(name: string, run: (c: C) => void): BtNode<C> => ({
  name,
  tick: (c) => {
    run(c);
    c.trace.push(name);
    return "sucesso";
  },
});

/** Tentativa: uma ação que pode não ter como ser feita (devolve false) e deixa o seletor tentar o próximo ramo. */
export const attempt = <C extends Traced>(name: string, run: (c: C) => boolean): BtNode<C> => ({
  name,
  tick: (c) => {
    if (!run(c)) return "falha";
    c.trace.push(name);
    return "sucesso";
  },
});
